import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../../config/api';
import { FiUpload, FiTrash2, FiFileText, FiPlus, FiX, FiEdit } from 'react-icons/fi';
import { motion } from 'framer-motion';

const AdminResources = () => {
    const [resources, setResources] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({ file: null, logoFile: null, title: '', description: '', tag: 'General', grade: 'All Grades', image_path: '' });
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        fetchResources();
    }, []);

    const fetchResources = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/manage_resources.php`);
            const data = await response.json();
            if (data.status === 'success') {
                setResources(data.data);
            }
        } catch (error) {
            console.error("Error fetching resources:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleUpload = async (e) => {
        e.preventDefault();
        
        if (!editingId && (!formData.file || !formData.title)) return;
        if (editingId && !formData.title) return;

        setUploading(true);
        const data = new FormData();
        if (formData.file) {
            data.append('file', formData.file);
        }
        data.append('title', formData.title);
        data.append('description', formData.description);
        data.append('tag', formData.tag);
        data.append('grade', formData.grade);

        if (editingId) {
            data.append('id', editingId);
        }
        if (formData.logoFile) {
            data.append('logo', formData.logoFile);
        }

        try {
            const response = await fetch(`${API_BASE_URL}/manage_resources.php`, {
                method: 'POST',
                body: data
            });
            
            if (!response.ok) {
                const text = await response.text();
                throw new Error(`HTTP ${response.status}: ${text}`);
            }

            const result = await response.json();
            if (result.status === 'success') {
                alert(`Resource ${editingId ? 'updated' : 'uploaded'} successfully!`);
                closeModal();
                fetchResources();
            } else {
                alert(result.message);
            }
        } catch (error) {
            console.error(error);
            alert("Action failed: " + error.message);
        } finally {
            setUploading(false);
        }
    };

    const openModal = (res = null) => {
        if (res) {
            setEditingId(res.id);
            setFormData({
                file: null,
                logoFile: null,
                title: res.title,
                description: res.description,
                tag: res.tag || 'General',
                grade: res.grade || 'All Grades',
                image_path: res.image_path || ''
            });
        } else {
            setEditingId(null);
            setFormData({ file: null, logoFile: null, title: '', description: '', tag: 'General', grade: 'All Grades', image_path: '' });
        }
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingId(null);
        setFormData({ file: null, logoFile: null, title: '', description: '', tag: 'General', grade: 'All Grades', image_path: '' });
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this resource?")) return;

        try {
            const response = await fetch(`${API_BASE_URL}/manage_resources.php?id=${id}`, {
                method: 'DELETE'
            });
            const result = await response.json();
            if (result.status === 'success') {
                setResources(resources.filter(res => res.id !== id));
            }
        } catch (error) {
            alert("Delete failed");
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold text-slate-800">Learning Resources</h3>
                <button 
                    onClick={() => openModal()}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg font-bold flex items-center gap-2 hover:bg-blue-700 transition-colors shadow-sm"
                >
                    <FiPlus /> Add PDF
                </button>
            </div>

            {loading ? (
                <div className="h-64 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
            ) : (
                <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
                    <table className="w-full text-left">
                        <thead className="bg-slate-50">
                            <tr>
                                <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Resource</th>
                                <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Grade & Tag</th>
                                <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Description</th>
                                <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Date</th>
                                <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-right">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {resources.map(res => (
                                <tr key={res.id} className="hover:bg-slate-50 transition-colors">
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 bg-blue-50 text-blue-600 rounded-lg"><FiFileText /></div>
                                            <span className="font-bold text-slate-700">{res.title}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex flex-col gap-1 items-start">
                                            <span className="px-3 py-0.5 bg-orange-100 text-orange-600 rounded-full text-[9px] font-black uppercase tracking-widest shrink-0">
                                                {res.grade || 'All Grades'}
                                            </span>
                                            <span className="px-3 py-0.5 bg-slate-100 text-slate-600 rounded-full text-[9px] font-black uppercase tracking-widest shrink-0">
                                                {res.tag || 'General'}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-slate-500 max-w-xs truncate text-sm">{res.description}</td>
                                    <td className="px-6 py-4 text-slate-400 text-xs">{new Date(res.created_at).toLocaleDateString()}</td>
                                    <td className="px-6 py-4 text-right flex justify-end gap-3">
                                        <button 
                                            onClick={() => openModal(res)}
                                            className="text-blue-500 hover:text-blue-700 transition-colors"
                                            title="Edit Resource"
                                        >
                                            <FiEdit size={18} />
                                        </button>
                                        <button 
                                            onClick={() => handleDelete(res.id)}
                                            className="text-red-400 hover:text-red-600 transition-colors"
                                            title="Delete Resource"
                                        >
                                            <FiTrash2 size={18} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
                    <motion.div 
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden max-h-[90vh] flex flex-col"
                    >
                        <div className="flex justify-between items-center p-6 border-b border-slate-100 shrink-0">
                            <h3 className="text-lg font-bold text-slate-800">{editingId ? 'Edit Resource' : 'Add New PDF'}</h3>
                            <button onClick={closeModal} className="text-slate-400 hover:text-slate-600"><FiX size={20} /></button>
                        </div>
                        <div className="overflow-y-auto p-6">
                            <form onSubmit={handleUpload} className="space-y-4">
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">Title</label>
                                <input 
                                    type="text" 
                                    className="w-full px-4 py-2 rounded-lg bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-blue-500 outline-none"
                                    value={formData.title}
                                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-1">Grade / Level</label>
                                    <select 
                                        className="w-full px-4 py-2 rounded-lg bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-blue-500 outline-none appearance-none"
                                        value={formData.grade}
                                        onChange={(e) => setFormData({ ...formData, grade: e.target.value })}
                                        required
                                    >
                                        <option value="All Grades">All Grades</option>
                                        <option value="Grade 6">Grade 6</option>
                                        <option value="Grade 7">Grade 7</option>
                                        <option value="Grade 8">Grade 8</option>
                                        <option value="Grade 9">Grade 9</option>
                                        <option value="Grade 10">Grade 10</option>
                                        <option value="Grade 11">Grade 11 (O/L)</option>
                                        <option value="A/L 1st Year">A/L 1st Year</option>
                                        <option value="A/L 2nd Year">A/L 2nd Year</option>
                                        <option value="A/L">A/L General</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-1">Category Tag</label>
                                    <select 
                                        className="w-full px-4 py-2 rounded-lg bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-blue-500 outline-none appearance-none"
                                        value={formData.tag}
                                        onChange={(e) => setFormData({ ...formData, tag: e.target.value })}
                                        required
                                    >
                                        <option value="General">General</option>
                                        <option value="Notes">Notes / Theory</option>
                                        <option value="Tutes">Tutes</option>
                                        <option value="Past Papers">Past Papers</option>
                                        <option value="Model Papers">Model Papers</option>
                                        <option value="Syllabus">Syllabus</option>
                                        <option value="Assignments">Assignments</option>
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">Description</label>
                                <textarea 
                                    className="w-full px-4 py-2 rounded-lg bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-blue-500 outline-none h-20 resize-none"
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                />
                            </div>
                            <div className="flex gap-4">
                                <div className="flex-1">
                                    <label className="block text-sm font-bold text-slate-700 mb-1">LOGO IMAGE (Optional)</label>
                                    {formData.image_path && !formData.logoFile && (
                                        <div className="mb-2">
                                            <img src={`${API_BASE_URL}/../${formData.image_path}`} alt="Current Logo" className="h-12 w-12 object-contain rounded border border-slate-200 bg-slate-50 p-1" />
                                        </div>
                                    )}
                                    <input 
                                        type="file" 
                                        accept="image/*"
                                        className="w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                                        onChange={(e) => setFormData({ ...formData, logoFile: e.target.files[0] })}
                                    />
                                </div>
                            </div>
                            {!editingId && (
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-1">PDF File</label>
                                    <input 
                                        type="file" 
                                        accept=".pdf"
                                        className="w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                                        onChange={(e) => setFormData({ ...formData, file: e.target.files[0] })}
                                        required={!editingId}
                                    />
                                </div>
                            )}
                            <button 
                                type="submit"
                                disabled={uploading}
                                className="w-full bg-blue-600 text-white py-3 rounded-lg font-bold hover:bg-blue-700 transition-colors disabled:opacity-50 mt-4"
                            >
                                {uploading ? 'Saving...' : (editingId ? 'Save Changes' : 'Add Resource')}
                            </button>
                            </form>
                        </div>
                    </motion.div>
                </div>
            )}
        </div>
    );
};

export default AdminResources;
